/**
 * Idea here is use this as a mixin with your own UI-component class or object. This gives class keyboard navigability. There are some
 * things to consider when doing things like this. I recommend reading this article: https://developer.mozilla.org/en-US/docs/Web/Accessibility/Keyboard-navigable_JavaScript_widgets
 *
 * These functions implement way to control keyboard navigation inside a UI-component via HTMlElement.focus() function.
 *
 * @example
 *
 */

abstract class ControlMode {

  /**
   * All HTMLElement that are focusable by keyboard are stored here. Inputs or selectable values you name it.
   */
  protected focusableElements:HTMLElement[];

  /**
   * @constructor
   * @param {HTMlElement[]} focusableElements all keyboard focusable HTMLElement objects
   */
  constructor( focusableElements:HTMLElement[] ) {
    this.focusableElements = focusableElements;
  }

  public setFocusableElements( focusableElements:HTMLElement[] ) {
    this.focusableElements = focusableElements;
  }

  /**
   * Return next focusable HTMLElement
   * @param {HTMLElement} focusedElement current focused element
   * @returns {HTMLElement} next focusable element
   */
  public abstract findNextFocusableElement(focusedElement:HTMLElement):HTMLElement;

  /**
   * Return previous focusable HTMLElement
   * @param {HTMLElement} focusedElement current focused element
   * @returns {number} previous focuced element index
   */
  public abstract findPreviousFocusableElement(focusedElement:HTMLElement):HTMLElement;

  /**
   * Find focused element index inside the focucable elements list
   * @param {HTMlElement} focusedElement  find next focusable element
   * @returns {number} element index in focucedelements array
   */
   protected findFocusedElementIndex( focusedElement:HTMLElement ):number {
    return this.focusableElements.findIndex( element => element === focusedElement );
  }
}


/**
 * This navigation control mode stop at first and last element focusable elemnt.
 */
class NonRollingControlMode extends ControlMode  {

  /**
   * @constructor
   * @param focusableElements elements
   */
  constructor( focusableElements:HTMLElement[] ) {
    super(focusableElements);
  }

  public findNextFocusableElement( focusedElement:HTMLElement ):HTMLElement {
    const index = this.findFocusedElementIndex( focusedElement );

    if( index + 1 <= this.focusableElements.length ) {
      return this.focusableElements[ index + 1 ];
    }
  }

  public findPreviousFocusableElement(focusedElement:HTMLElement):HTMLElement {
    const index = this.findFocusedElementIndex( focusedElement );

    if( index - 1 >= 0 ) {
      return this.focusableElements[ index - 1 ];
    }
  }
}

/**
 * This navigation control mode where navigation rolls over and over. When user presses down button and the component focus is in the last focusable element.
 * The focus is taken to first focusable element. In a sense focus travels round and round and not stopping at the ends of focusable element list.
 */
class RollingControlMode extends ControlMode {

  public findNextFocusableElement( focusedElement:HTMLElement ) {
    const index = this.findFocusedElementIndex( focusedElement );

    if( index + 1 >= this.focusableElements.length ) {
      return this.focusableElements[ 0 ];
    } else {
      return this.focusableElements[ index + 1 ];
    }
  }

  /**
   * Find previous focused element
   * @param {HTMLElement} focusedElement current focused element
   * @returns {HTMlElement} previous focudes element
   */
   public  findPreviousFocusableElement(focusedElement:HTMLElement):HTMLElement {
    const index = this.findFocusedElementIndex( focusedElement );

    if( index - 1 < 0 ) {
      return this.focusableElements[ this.focusableElements.length - 1 ];
    } else {
      return this.focusableElements[ index - 1 ];
    }
  }
}

/**
 * Keyboard event types
 */
enum KeyboardNavigableEventTypes {
  ON_ENTER_PRESSED = 'onEnterPressed',
  ON_SPACE_PRESSED = 'onSpacePressed',
  ON_TAB_PRESSED = 'onTabPressed'
}

/**
 * Event types that subcribers can to subcribe to listen.
 */
type Subscribers = {
  [key in KeyboardNavigableEventTypes]: Function[];
}

class KeyboardNavigable {

  private containerElement?:HTMLElement;

  /**
   * Currently focused element
   */
  private focusedElement?:HTMLElement;

  /**
   * Component's focusable elements
   */
  protected focusableElements:HTMLElement[];

  /**
   * Keyboard control mode
   */
  private controlMode:ControlMode;

  private subcribers:Subscribers = {
    [KeyboardNavigableEventTypes.ON_ENTER_PRESSED]:[],
    [KeyboardNavigableEventTypes.ON_SPACE_PRESSED]:[],
    [KeyboardNavigableEventTypes.ON_TAB_PRESSED]:[]
  };

  /**
   * @constructor
   * @param {ControlMode} controlMode keyboard control behavior is defined by this
   * @param {HTMLElement} [containerElement] container HTMLElement object, pass falsy value to ignore this
   */
  constructor( controlMode:ControlMode, containerElement?:HTMLElement ) {
    this.controlMode = controlMode;
    this.containerElement = containerElement;
  }

 /**
  * Set keyboard focusable elements. Can be called when there is a need to update them, or
  * just set them in the time of a page load.
  */
 public setFocusableElements( focusableElements:HTMLElement[] ) {
    this.focusableElements = focusableElements;
  }

  /**
   * Subscribe to listen event
   * @param eventType listened event type
   * @param listener event listener function
   */
  public subscribe( eventType:KeyboardNavigableEventTypes, listener:Function ) {
    if( !Object.keys( this.subcribers ) ) {
      throw new Error("Allowed event types are " + Object.keys( this.subcribers ) );
    }

    this.subcribers[eventType].push( listener );
  }

  /**
   * Bind event listeners to keyboard navitable element
   * @param {HTMLElement} element - element to bind event listeners
   */
  bindKeyboardNavitable(element:HTMLElement) {
    element.addEventListener('keydown', this._onKeyDown.bind(this));
  }

  /**
   * Update focusable elements by calling this
   * @param {HTMLElement[]} focusableElements all focusable elements
   */
   protected setFocusaleElements( focusableElements:HTMLElement[] ) {
    this.focusableElements = Array.from( focusableElements );
  }

  /**
   * Move focus to a element
   * @public
   * @param {HTMLElement} focusedElement where focus is moved next
   */
  setFocusedElement(focusedElement:HTMLElement) {
    if( focusedElement ) {
      this.focusedElement = focusedElement;
      this.focusedElement.focus();
    }
  }

  /**
   * Set keyboard group element, the one element that is `tabindex="0"`-
   * @param {HTMLElement} element group element
   */
  setKeyboarGroupElement( element:HTMLElement ) {
    this.containerElement = element;
  }

  /**
   * When a user presses keydown
   * @param {Event} event - Key down event
   * @returns {boolean} false - Stop scrolling user press down or up
   */
  _onKeyDown(event:KeyboardEvent) {

    //Okey this bit is little messy..
    if( event.shiftKey && event.code === 'Tab' ) {//Shift + tab = focus moves out from the element

      //Change "container" element tabindex for a moment to -1 to allow user tab outside of the element
      this.containerElement?.setAttribute('tabindex', '-1');

      setTimeout(() => {
        this.containerElement?.setAttribute('tabindex', '0');//Allow tabbing back to the element
      },200);

      this.publish( KeyboardNavigableEventTypes.ON_TAB_PRESSED );

      return false;
    }
    else if( event.code === 'Tab' ) {//Tabout
      this.publish( KeyboardNavigableEventTypes.ON_TAB_PRESSED );
    }

    //Up and down
    if( ['ArrowDown','ArrowUp'].includes(event.code) ) {
      event.preventDefault();//Stop scrolling
      event.stopPropagation();//Stop scrolling
    }

    if( event.code === 'ArrowDown' ) {//Arrow down
      this.setFocusedElement( this.controlMode.findNextFocusableElement( this.focusedElement ) );
    }

    if( event.code === 'ArrowUp' ) {//Arrow up
      this.setFocusedElement( this.controlMode.findPreviousFocusableElement( this.focusedElement ) );
    }

    if( event.code === 'Enter' ) {//Enter
      this.publish( KeyboardNavigableEventTypes.ON_ENTER_PRESSED, { focusedElement: this.focusedElement}  )
    }

    if( event.code === 'Space' ) {
      this.publish( KeyboardNavigableEventTypes.ON_SPACE_PRESSED, { focusedElement: this.focusedElement} )
    }

    return false;
  }

  /**
   * Publish or fires event and send notification to subcribed listeners
   * @param eventType event type
   * @param payload payload data
   */
   private publish(eventType:KeyboardNavigableEventTypes, payload?:any) {
    this.subcribers[eventType].forEach((callBack) => {
      callBack( payload );
    })
  }
}

export {
  KeyboardNavigable,
  NonRollingControlMode,
  RollingControlMode,
  KeyboardNavigableEventTypes
}
